import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z, ZodBoolean, ZodOptional, ZodString } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useViewerIsSuperAdmin } from "@/lib/rights/use-viewer-is-baback-team";
import {
  CarrierCodePickableType,
  ConnectionInfo,
  FieldInfo,
  useCarriersVariables,
} from "../use-carriers-variables";
import {
  CreateCarrierDto,
  UpdateCarrierDto,
} from "@babackapp/private-api-client/types";

const getZodSchema = (carrierConnectionFields: ConnectionInfo) => {
  const carrierFields = Object.entries(carrierConnectionFields);
  const credentialsFormShape: {
    [key: string]: ZodOptional<ZodString> | ZodOptional<ZodBoolean> | ZodString;
  } = {};

  carrierFields.forEach(([key, field]: [string, FieldInfo]) => {
    credentialsFormShape[key] =
      field.type === "boolean"
        ? z.boolean().optional()
        : field.required
          ? z.string().min(1, { message: `${field.label} is required` })
          : z.string().optional();
  });

  return z.object(credentialsFormShape);
};

type CarrierTransportZFormValues = z.infer<ReturnType<typeof getZodSchema>>;

export const useCarrierTransportForm = ({
  defaultValues,
  onSubmit,
}: {
  defaultValues?: {
    code: CarrierCodePickableType;
    credentials: { [key: string]: string };
    title?: string | null;
  };
  onSubmit: (data: CreateCarrierDto | UpdateCarrierDto) => void;
}) => {
  const [carrierCodeSelected, setCarrierCodeSelected] = useState<
    CarrierCodePickableType | undefined
  >(defaultValues ? defaultValues?.code : undefined);

  const { listCarriers, isLoading } = useCarriersVariables();

  // The name the merchant sees instead of the code, e.g. the contract behind a
  // Shippingbo TMS carrier. Staff-set, so only staff get the field.
  const isSuperAdmin = useViewerIsSuperAdmin();
  const [title, setTitle] = useState(defaultValues?.title ?? "");

  // Derived, not stored: the catalog arrives from the API after the first
  // render, and the edit dialog opens with a carrier already selected.
  const carrierFormFields = useMemo(
    () =>
      carrierCodeSelected
        ? listCarriers.find(
            (carrier) =>
              carrier.value.toLocaleLowerCase() ===
              carrierCodeSelected.toLocaleLowerCase(),
          )?.connectionInfo
        : undefined,
    [listCarriers, carrierCodeSelected],
  );

  const carrierFormSchema = useMemo(
    () => getZodSchema(carrierFormFields ?? {}),
    [carrierFormFields],
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid, dirtyFields },
    reset: resetForm,
    trigger,
    getValues,
    setValue,
  } = useForm<CarrierTransportZFormValues>({
    resolver: zodResolver(carrierFormSchema),
    mode: "onChange",
    defaultValues: {
      ...defaultValues?.credentials,
    },
  });

  // `isValid` is only recomputed on input: without this, an edit dialog
  // opened before the catalog loaded keeps the verdict of the empty schema.
  useEffect(() => {
    if (!carrierFormFields) return;
    // A stored value the select does not offer (a Chronopost service typed in
    // the database, say) would show blank yet be sent, and the API refuses
    // it. Clearing it makes the required select block the form instead.
    Object.entries(carrierFormFields).forEach(([key, field]) => {
      const value = getValues(key);
      const isUnknownOption =
        field.type === "select" &&
        typeof value === "string" &&
        value !== "" &&
        !field.options?.some((option) => option.value === value);
      if (isUnknownOption) {
        setValue(key, "");
      }
    });
    trigger();
  }, [carrierFormSchema]);

  const onChangeCarrierPicked = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      resetForm({
        code: e.target.value,
      });

      setCarrierCodeSelected(
        (e.target.value || undefined) as CarrierCodePickableType | undefined,
      );
    },
    [],
  );

  return {
    register,
    handleSubmit: handleSubmit((data) => {
      if (!carrierCodeSelected) return;
      // Only the fields of the selected carrier: the edit dialog seeds the
      // form with every stored preference.
      const fieldKeys = Object.keys(carrierFormFields ?? {});
      const stored = defaultValues?.credentials;
      // A switch renders `false` when the preference was never stored. On an
      // edit, sending it would write a value the merchant never chose; the
      // API merges, so leaving it out keeps the stored state.
      const isUntouchedUnsetSwitch = (key: string) =>
        !!stored &&
        carrierFormFields?.[key]?.type === "boolean" &&
        stored[key] === undefined &&
        !dirtyFields[key];
      // On an edit, an emptied name is sent so that it clears; a new carrier
      // just goes without one.
      const isEdit = !!defaultValues?.code;
      const titleToSend = isSuperAdmin
        ? isEdit
          ? title.trim()
          : title.trim() || undefined
        : undefined;
      onSubmit({
        code: carrierCodeSelected,
        credentials: Object.fromEntries(
          Object.entries(data).filter(
            ([key]) => fieldKeys.includes(key) && !isUntouchedUnsetSwitch(key),
          ),
        ),
        ...(titleToSend !== undefined && { title: titleToSend }),
      });
    }),
    control,
    errors,
    // No catalog entry for the carrier being edited (a legacy code the picker
    // never offers): nothing to validate, so nothing to send.
    isValid: isValid && !!carrierFormFields,
    isLoading,
    carrierFormFields,
    onChangeCarrierPicked,
    carrierCodeSelected,
    canEditTitle: isSuperAdmin,
    title,
    setTitle,
  };
};
